(() => {
  if (new URLSearchParams(location.search).get('hiool_camera_diag') !== '1') return;
  if (window.__HIOOL_VISUAL_VALIDATION_BOOT__) return;

  window.__HIOOL_VISUAL_VALIDATION_BOOT__ = {
    loaded: true,
    loadedAt: Date.now(),
    src: document.currentScript?.src || null,
  };

  const TARGETS = [
    {
      key: 'bomba',
      title: "Trader Joe's Italian Bomba Hot Pepper Sauce 6.7 oz",
      handle: 'trader-joes-italian-bomba-hot-pepper-sauce-6-7oz',
    },
    {
      key: 'kilimanjaro',
      title: "Trader Joe's Kilimanjaro Single Origin Instant Coffee",
      handle: 'trader-joes-single-origin-kilimanjaro-instant-coffee-1-16-oz',
    },
  ];

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function waitForEngine(timeoutMs = 20000) {
    const started = performance.now();
    while (performance.now() - started < timeoutMs) {
      if (window.HIOOLVisualSearchPCA192?.search) return window.HIOOLVisualSearchPCA192;
      await sleep(50);
    }
    throw new Error('HIOOLVisualSearchPCA192 did not become available.');
  }

  function ensureNode() {
    let node = document.getElementById('hiool-visual-validation');
    if (!node) {
      node = document.createElement('div');
      node.id = 'hiool-visual-validation';
      node.hidden = true;
      node.dataset.status = 'running';
      document.documentElement.appendChild(node);
    }
    return node;
  }

  function write(node, payload, status = 'running') {
    node.dataset.status = status;
    node.textContent = JSON.stringify(payload);
    window.__HIOOL_VISUAL_VALIDATION__ = payload;
  }

  function normalizeImage(value) {
    if (!value) return null;
    if (typeof value === 'string') return value.startsWith('//') ? `https:${value}` : value;
    const raw = value.src || value.url || value.originalSrc || null;
    return raw?.startsWith('//') ? `https:${raw}` : raw;
  }

  async function imageInfo(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ url, width: img.naturalWidth, height: img.naturalHeight, ok: true });
      img.onerror = () => resolve({ url, width: 0, height: 0, ok: false });
      img.src = url;
    });
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Product JSON failed for ${handle}: ${response.status}`);
    return response.json();
  }

  async function loadIndexedCatalog(engine) {
    const config = engine.getConfig?.() || {};
    const manifestUrl = config.resolvedManifestUrl;
    if (!manifestUrl) throw new Error('PCA manifest URL unavailable from engine config.');
    const manifestResponse = await fetch(manifestUrl, { cache: 'no-store', credentials: 'same-origin' });
    if (!manifestResponse.ok) throw new Error(`PCA manifest failed: ${manifestResponse.status}`);
    const manifest = await manifestResponse.json();
    const chunks = [];
    const chunkUrls = [];
    for (const entry of manifest.chunks || []) {
      const filename = /^hiool-pca192-/.test(entry.file) ? entry.file : `hiool-pca192-${entry.file}`;
      let url;
      if (typeof config.chunkUrl === 'function') {
        url = config.chunkUrl(entry.file);
      } else {
        const resolved = new URL(filename, manifestUrl);
        const manifestSearch = new URL(manifestUrl).search;
        if (manifestSearch && !resolved.search) resolved.search = manifestSearch;
        url = resolved.href;
      }
      const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error(`PCA chunk failed: ${response.status} ${url}`);
      chunkUrls.push(url);
      chunks.push(await response.json());
    }
    return {
      manifest,
      products: chunks.flatMap((chunk) => chunk.products || []),
      manifestUrl,
      chunkUrls,
    };
  }

  function compact(result) {
    return {
      backend: result.backend || null,
      margin: result.margin,
      top5: (result.results || []).slice(0, 5).map((row, index) => ({
        rank: index + 1,
        title: row.title,
        handle: row.handle,
        score: row.score,
        maxScore: row.maxScore,
        centroidScore: row.centroidScore,
      })),
    };
  }

  async function runSearch(engine, imageUrl) {
    const info = await imageInfo(imageUrl);
    if (!info.ok || !info.width || !info.height) throw new Error(`Image failed to load: ${imageUrl}`);
    const started = performance.now();
    const result = await engine.search(imageUrl, { topK: 5 });
    return {
      sourceImage: info,
      elapsedMs: performance.now() - started,
      ...compact(result),
    };
  }

  async function validateTarget(engine, catalog, target) {
    const indexed = catalog.products.find((p) => p.handle === target.handle)
      || catalog.products.find((p) => String(p.title || '').toLowerCase().includes(target.title.toLowerCase()));
    if (!indexed) throw new Error(`Indexed product not found: ${target.handle}`);

    const product = await fetchProduct(target.handle);
    const productImages = [...new Set([
      ...(Array.isArray(product.images) ? product.images : []),
      product.featured_image,
    ].map(normalizeImage).filter(Boolean))];

    const primaryUrl = normalizeImage(indexed.image) || productImages[0];
    if (!primaryUrl) throw new Error(`No primary image found: ${target.handle}`);
    const secondaryUrl = productImages.find((url) => url !== primaryUrl) || null;

    const primary = await runSearch(engine, primaryUrl);
    const secondary = secondaryUrl ? await runSearch(engine, secondaryUrl) : null;
    const primaryRank = primary.top5.findIndex((x) => x.handle === target.handle) + 1;
    const secondaryRank = secondary ? secondary.top5.findIndex((x) => x.handle === target.handle) + 1 : null;

    return {
      expectedTitle: target.title,
      expectedHandle: target.handle,
      indexedTitle: indexed.title,
      indexedPrimaryImage: primaryUrl,
      productImageCount: productImages.length,
      primary: {
        ...primary,
        expectedRank: primaryRank || null,
        pass: primaryRank === 1,
      },
      secondary: secondary ? {
        ...secondary,
        expectedRank: secondaryRank || null,
        pass: secondaryRank > 0 && secondaryRank <= 3,
      } : {
        available: false,
        pass: true,
      },
    };
  }

  async function main() {
    const node = ensureNode();
    const output = {
      status: 'running',
      startedAt: new Date().toISOString(),
      boot: window.__HIOOL_VISUAL_VALIDATION_BOOT__,
    };
    write(node, output);

    try {
      const engine = await waitForEngine();
      output.runtimeBefore = engine.getDebugState?.() || null;
      output.selfTest = await engine.selfTest();
      if (!output.selfTest?.ok) throw new Error(`PCA selfTest failed: ${output.selfTest?.error || 'unknown'}`);

      const preloadStart = performance.now();
      await engine.preload();
      output.preloadElapsedMs = performance.now() - preloadStart;
      output.runtimeReady = engine.getDebugState?.() || null;

      const catalog = await loadIndexedCatalog(engine);
      output.catalog = {
        products: catalog.products.length,
        chunks: catalog.manifest?.chunks?.length || 0,
        manifestUrl: catalog.manifestUrl,
        chunkUrls: catalog.chunkUrls,
      };

      for (const target of TARGETS) {
        output[target.key] = await validateTarget(engine, catalog, target);
        write(node, output);
      }

      const visualPass = TARGETS.every(({ key }) => {
        const row = output[key];
        return row?.primary?.pass === true && row?.secondary?.pass === true;
      });
      output.status = visualPass ? 'pass' : 'fail';
      output.finishedAt = new Date().toISOString();
      write(node, output, output.status);
    } catch (error) {
      output.status = 'fail';
      output.error = {
        message: String(error?.message || error),
        stack: error?.stack || null,
      };
      output.runtimeAfterError = window.HIOOLVisualSearchPCA192?.getDebugState?.() || null;
      output.finishedAt = new Date().toISOString();
      write(node, output, 'fail');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', main, { once: true });
  } else {
    main();
  }
})();
